// OPS Occult Archive visual-source refinement — Wound Man / historical medical diagram.
(()=>{
  const pack=window.OPS_OCCULT_EDITORIAL;
  if(!pack||!Array.isArray(pack.entries)) return;

  const duplicate=pack.entries.some((x)=>{
    const hay=[x&&x.title,x&&x.slug,x&&x.synopsis].filter(Boolean).join(' ').toLowerCase();
    return hay.includes('wound man')||hay.includes('wundenmann');
  });
  if(duplicate) return;

  pack.entries.push({
    title:'Wound Man',
    slug:'wound-man-medieval-injury-diagram',
    type:'Historical medicine, anatomy & diagrammatic figure',
    depth:'Deep Water',
    synopsis:'A late-medieval body diagram that turns a standing human figure into an index of injuries, weapons and vulnerable points.',
    reference:{
      period:'Mid-15th century manuscript tradition',
      place:'England / European medical manuscript culture',
      tradition:'Surgery, anatomy & wound diagrams',
      status:'Historical visual source with rights-verified digital image'
    },
    illustration:{
      url:'https://commons.wikimedia.org/wiki/Special:Redirect/file/Wound%20man%2C%20Pseudo-Galen%2C%20Anathomia%3B%20WMS%20290%20Wellcome%20L0013467.jpg?width=1400',
      alt:'Mid-15th-century Wound Man from an English anatomical manuscript: a standing human figure marked by arrows, blades, blows and animal bites, with Latin labels describing injuries.'
    },
    lead:[
      'Wound Man is useful to artists because the body is used as an information surface rather than merely an anatomical study. Weapons, wounds, animals and labels create a dense field of diagonals around one upright silhouette, making the page simultaneously figure drawing, object catalogue and diagram.',
      'This version comes from Pseudo-Galen, Anathomia, Wellcome manuscript WMS 290. Wellcome describes Wound Man figures as a 15th- and 16th-century tradition whose exact purpose remains uncertain, probably serving as a reminder of injuries to which the body is prone. The selected high-resolution Wellcome reproduction is identified on Wikimedia Commons as CC BY 4.0.'
    ],
    sections:[
      {
        heading:'Artist research: use the body as an index',
        paragraphs:[
          'For illustration, album art, printmaking and worldbuilding, study the composition before the individual wounds: one calm frontal figure, a ring of incoming objects, repeated diagonals and short labels locating damage. That structure can inspire an original armour weak-point chart, fictional field-surgery page, cursed-body map or character-history diagram without copying the manuscript as a finished design.',
          'For tattoo reference, the complete image is too dense at most sizes. Keep the body silhouette and only a few enlarged weapon or wound relationships, simplify the manuscript hatching, remove tiny Latin labels unless they are researched and intentionally reproduced, and leave enough negative space so each diagonal remains readable when healed.',
          'Treat the image as a historical medical and manuscript source, not as a generic symbol of violence. If adapting specific weapons or captions, research what they depict and what the labels mean rather than turning unfamiliar historical notation into decorative filler.'
        ]
      },
      {
        heading:'Follow the diagram tradition',
        paragraphs:[
          'Compare Wound Man with Zodiac Man, bloodletting figures, surgical manuals and later anatomical plates. The repeatable idea is the body as a navigable graphic system: different traditions attach injuries, zodiac correspondences, incision points or organs to the same central human form.',
          'For creature and prop designers, the surrounding weapon silhouettes are a separate research trail: arrows, blades, clubs and animal attacks can be studied as period forms, then redrawn accurately or transformed into new fictional object families.'
        ]
      }
    ],
    evidence:{
      label:'Rights-verified historical image',
      note:'Wikimedia Commons identifies the Wellcome Library reproduction L0013467 as Creative Commons Attribution 4.0 International (CC BY 4.0). Commercial reuse and adaptation are permitted with attribution, a licence link and indication of changes when applicable.'
    },
    visualResearch:{
      creator:'Unknown manuscript artist; work catalogued as Pseudo-Galen, Anathomia',
      date:'Mid-15th century',
      work:'Wound man, Pseudo-Galen, Anathomia; WMS 290',
      institution:'Wellcome Collection, London',
      imageReference:'L0013467',
      manuscriptReference:'WMS 290',
      provenance:'English anatomical manuscript; Wellcome Collection describes the figure as part of the Wound Man tradition found in 15th- and 16th-century manuscripts and printed books.',
      rights:'Creative Commons Attribution 4.0 International (CC BY 4.0)',
      rightsUrl:'https://creativecommons.org/licenses/by/4.0/',
      attribution:'Wellcome Library, London; image reference L0013467',
      verifiedOn:'2026-09-06'
    },
    sources:[
      {
        label:'Wikimedia Commons — Wound man, Pseudo-Galen, Anathomia; WMS 290, Wellcome L0013467 (CC BY 4.0)',
        url:'https://commons.wikimedia.org/wiki/File:Wound_man,_Pseudo-Galen,_Anathomia;_WMS_290_Wellcome_L0013467.jpg',
        type:'Rights-verified image source'
      },
      {
        label:'Wellcome Collection — Wound man, Pseudo-Galen, Anathomia; WMS 290',
        url:'https://wellcomecollection.org/works/tp6fppqz',
        type:'Institutional context & provenance'
      },
      {
        label:'Creative Commons — Attribution 4.0 International',
        url:'https://creativecommons.org/licenses/by/4.0/',
        type:'Reuse licence'
      }
    ],
    related:['Zodiac Man (Homo Signorum)','Anatomy','Historical medicine'],
    catalogue:[]
  });
})();

// OPS Occult Archive visual-source refinement — Rosicrucian Sophia / emblematic body map.
(()=>{
  const pack=window.OPS_OCCULT_EDITORIAL;
  if(!pack||!Array.isArray(pack.entries)) return;

  const duplicate=pack.entries.some((x)=>{
    const hay=[x&&x.title,x&&x.slug,x&&x.synopsis].filter(Boolean).join(' ').toLowerCase();
    return hay.includes('sophiamystical')||hay.includes('sophia mystica')||hay.includes('rosicrucian sophia');
  });
  if(duplicate) return;

  pack.entries.push({
    title:'Sophia Mystica — Rosicrucian Body Map',
    slug:'sophia-mystica-rosicrucian-body-map-1785',
    type:'Rosicrucianism, emblem book & symbolic figure',
    depth:'Deep Water',
    synopsis:'A richly layered 1785 Rosicrucian plate turns the allegorical figure of Sophia into a vertical map of text, elemental circles and correspondences.',
    reference:{
      period:'1785',
      place:'Altona, Germany',
      tradition:'Rosicrucian and Christian-esoteric emblematic print culture',
      status:'Historical visual source; Wikimedia Commons marks the selected image public domain'
    },
    illustration:{
      url:'https://commons.wikimedia.org/wiki/Special:Redirect/file/SophiaMystical.jpg?width=1400',
      alt:'Colour Rosicrucian emblem from 1785 showing a crowned female allegorical figure of Sophia arranged as a vertical symbolic diagram, with circles, inscriptions and celestial and elemental imagery.'
    },
    lead:[
      'This plate is useful as a study in symbolic hierarchy rather than as a ready-made occult design. A single upright figure holds together many different visual systems: portrait, costume, circles, labels, elemental groupings and celestial imagery. The eye reads the human silhouette first, then discovers smaller correspondences inside and around it.',
      'The image comes from Geheime Figuren der Rosenkreuzer, aus dem 16ten und 17ten Jahrhundert, published at Altona in 1785. Wikimedia Commons identifies the author as unknown, traces this colour version to the University of Wisconsin scan, and marks the file public domain and free of known copyright restrictions.'
    ],
    sections:[
      {
        heading:'Artist research: build a hierarchy, not a copy',
        paragraphs:[
          'For illustration, printmaking, graphic design, album art and game-world documents, study how the page combines one dominant silhouette with nested circles, short inscriptions, repeated colour accents and smaller symbols. Rebuild that hierarchy around an original subject: a fictional cosmology, character belief system, botanical body map or invented elemental scheme can use the same organisational logic without reproducing the historic plate.',
          'For tattoo reference, simplify aggressively. Keep one strong central figure or torso and perhaps three to five enlarged correspondences. Tiny German and Latin lettering, fine internal rules and densely packed labels will collapse at small scale; replace them only with researched text or with original symbols that belong to the new composition.',
          'The plate belongs to a specific Rosicrucian and Christian-esoteric context. Sophia here should not be flattened into a generic “occult goddess”. If a particular inscription, emblem or theological attribute is retained, follow it back to the source text and understand its role before adapting it.'
        ]
      },
      {
        heading:'Follow the emblem-book trail',
        paragraphs:[
          'Compare the plate with Rosicrucian emblem books, alchemical emblem series, Zodiac Man and other body-as-diagram traditions. The useful research question is how different historical systems attach abstract ideas to a figure while keeping the overall page readable.',
          'For lettering and layout research, separate the image into layers: headline, central figure, circular labels, side commentary and small symbolic clusters. Those relationships can inspire new poster, record-sleeve, bookplate or game-document systems without borrowing a contemporary artist’s finished design.'
        ]
      }
    ],
    evidence:{
      label:'Rights-verified historical image',
      note:'Wikimedia Commons marks SophiaMystical.jpg as public domain, identifies the underlying source as Geheime Figuren der Rosenkreuzer (Altona, 1785), and states that the file is free of known copyright restrictions. The Commons upload log traces the colour scan to the University of Wisconsin Digital Collections.'
    },
    visualResearch:{
      creator:'Unknown author / historical plate maker',
      date:'1785',
      work:'Geheime Figuren der Rosenkreuzer, aus dem 16ten und 17ten Jahrhundert',
      institution:'University of Wisconsin Digital Collections (scan provenance recorded by Wikimedia Commons)',
      provenance:'Published at Altona in 1785; the Commons file record identifies the colour image as derived from the University of Wisconsin digitisation of the historical volume.',
      rights:'Public domain; free of known copyright restrictions',
      rightsUrl:'https://commons.wikimedia.org/wiki/File:SophiaMystical.jpg',
      attribution:'Unknown author, Sophia from Geheime Figuren der Rosenkreuzer, Altona, 1785; scan provenance via University of Wisconsin Digital Collections / Wikimedia Commons.',
      verifiedOn:'2026-09-06'
    },
    sources:[
      {
        label:'Wikimedia Commons — SophiaMystical.jpg (public domain)',
        url:'https://commons.wikimedia.org/wiki/File:SophiaMystical.jpg',
        type:'Rights-verified image source & scan provenance'
      },
      {
        label:'Wielkopolska Digital Library — Geheime Figuren der Rosenkreuzer (1785)',
        url:'https://www.wbc.poznan.pl/publication/488831',
        type:'Institutional bibliographic record & public-domain copy'
      }
    ],
    related:['Zodiac Man (Homo Signorum)','Wound Man','Alchemy','Rosicrucianism','Emblem books'],
    catalogue:[]
  });
})();

// OPS Occult Archive visual-source refinement — Topsell Lamia / early-modern bestiary hybrid.
(()=>{
  const pack=window.OPS_OCCULT_EDITORIAL;
  if(!pack||!Array.isArray(pack.entries)) return;

  const duplicate=pack.entries.some((x)=>{
    const hay=[x&&x.title,x&&x.slug,x&&x.synopsis].filter(Boolean).join(' ').toLowerCase();
    return hay.includes('topsell lamia')||hay.includes('lamia topsell')||hay.includes('lamia-bestiary');
  });
  if(duplicate) return;

  pack.entries.push({
    title:'Lamia — Topsell’s Early-Modern Hybrid Monster',
    slug:'lamia-topsell-early-modern-bestiary-1658',
    type:'Folklore, bestiary & hybrid creature reference',
    depth:'Deep Water',
    synopsis:'A strange four-legged Lamia from Edward Topsell’s 1658 monster-and-animal book, useful for studying how early-modern print culture assembled unfamiliar creatures from recognisable animal parts.',
    reference:{
      period:'1658',
      place:'London, England',
      tradition:'Early-modern natural history, monster lore & bestiary illustration',
      status:'Historical visual source with rights-verified digital image'
    },
    illustration:{
      url:'https://commons.wikimedia.org/wiki/Special:Redirect/file/A%20Lamia%20Wellcome%20L0005336.jpg?width=1400',
      alt:'Black-and-white 1658 illustration of a Lamia from Edward Topsell: an imagined four-legged hybrid monster with an unusual humanlike face, animal body, patterned surface and long tail.'
    },
    lead:[
      'This Lamia is valuable as creature-design research because it does not look like the now-familiar woman-and-serpent formula. Topsell’s printed monster is assembled as a four-legged hybrid, with an uncanny face, strong quadruped stance, patterned body and tail. That mismatch between recognisable animal anatomy and impossible identity is exactly what makes historical bestiaries useful to artists designing original creatures.',
      'The selected image is Wellcome reference L0005336, from Edward Topsell’s The history of four-footed beasts and serpents, published in 1658. Wikimedia Commons records the Wellcome reproduction under Creative Commons Attribution 4.0 International (CC BY 4.0), permitting commercial reuse and adaptation with appropriate attribution, a licence link and indication of changes.'
    ],
    sections:[
      {
        heading:'Artist research: hybridise the logic, not the drawing',
        paragraphs:[
          'For illustration, printmaking, album art and game creature design, break the monster into decisions: head-to-body mismatch, leg rhythm, back line, surface pattern, tail direction and the amount of believable animal anatomy holding the impossible creature together. Build a new beast by changing those relationships or borrowing anatomy from different source animals rather than tracing Topsell’s finished outline.',
          'For tattoo reference, the silhouette is much more useful than the small printed texture. Keep the head, spine, four-leg stance and tail readable; simplify fine hatching and tiny facial marks; enlarge the important joints and leave clear negative space between legs and under the body so the creature remains legible after healing.',
          'Treat this as one early-modern printed interpretation of Lamia, not as a definitive or universal image of the myth. Lamia traditions changed across classical, medieval and later European sources, so a modern reinterpretation benefits from comparing several historical descriptions and images before choosing which features to carry forward.'
        ]
      },
      {
        heading:'Follow the monster-book trail',
        paragraphs:[
          'Use this image as an entry point into Topsell, Conrad Gessner, early-modern natural histories and illustrated monster books. Compare how printers move between observed animals, travellers’ reports, inherited classical stories and invented hybrids; the visual mistakes and contradictions are often more creatively useful than a polished modern fantasy bestiary.',
          'A practical research route is Lamia → hybrid beasts → serpents and dragons → classical monster traditions → Topsell and Gessner → early-modern woodcut and engraving styles. This also creates a useful bridge from the library’s heraldic beasts into folklore and creature-design research.'
        ]
      }
    ],
    evidence:{
      label:'Rights-verified historical image',
      note:'Wikimedia Commons identifies Wellcome image L0005336, A Lamia, as a reproduction from Edward Topsell’s The history of four-footed beasts and serpents (1658) and explicitly licenses the file under Creative Commons Attribution 4.0 International (CC BY 4.0).'
    },
    visualResearch:{
      creator:'Edward Topsell (book author/compiler); historical image maker not identified on the file record',
      date:'1658',
      work:'The history of four-footed beasts and serpents',
      institution:'Wellcome Collection, London',
      imageReference:'L0005336',
      provenance:'Wellcome Rare Books image from Topsell’s 1658 The history of four-footed beasts and serpents; Wikimedia Commons records the file and links to the Wellcome Collection work record.',
      rights:'Creative Commons Attribution 4.0 International (CC BY 4.0)',
      rightsUrl:'https://creativecommons.org/licenses/by/4.0/',
      attribution:'Wellcome Library, London; A Lamia, image reference L0005336, from Edward Topsell, The history of four-footed beasts and serpents (1658).',
      verifiedOn:'2026-09-06'
    },
    sources:[
      {
        label:'Wikimedia Commons — A Lamia Wellcome L0005336 (CC BY 4.0)',
        url:'https://commons.wikimedia.org/wiki/File:A_Lamia_Wellcome_L0005336.jpg',
        type:'Rights-verified image source'
      },
      {
        label:'Wellcome Collection — source record linked from image L0005336',
        url:'https://wellcomecollection.org/works/wb825ahg',
        type:'Institutional source & provenance'
      },
      {
        label:'Creative Commons — Attribution 4.0 International',
        url:'https://creativecommons.org/licenses/by/4.0/',
        type:'Reuse licence'
      }
    ],
    related:['Heraldry','Dragons','Serpents','Folklore','Bestiaries','Creature design'],
    catalogue:[]
  });
})();
